/**
 * Apresentação: o texto em corpo grande, para ler na tela.
 *
 * É a parte que "quase automaticamente" vira modo de leitura. Ela **não é** um
 * PDF, não é um download e não é zoom de navegador: é o mesmo texto — o que a
 * pessoa escreveu, sem as ferramentas do editor — desenhado em corpo grande,
 * **usando a tela toda**.
 *
 * Por que a tela toda como padrão: quem abre a apresentação quer ver melhor e ver
 * mais — num monitor de mesa, uma coluna de 150 mm deixa dois terços da tela
 * vazios. Quem preferir a medida de livro tem o botão **Coluna estreita**, que
 * volta para a largura salva no texto (a mesma do editor).
 *
 * O que a tela oferece, e por quê:
 *
 * - **A− / A+** com degraus grandes (20 a 56 pt, começando em 38): quem enxerga
 *   pouco precisa de degrau que mude de verdade;
 * - **Tela toda / Coluna estreita**: largura, sem mexer no texto;
 * - **Tela cheia**: tira o resto do site do caminho **e dobra o corpo** — é o modo
 *   de quem vai ler de longe;
 * - **fundo claro ou escuro**: quem lê à noite agradece, e o contraste muda;
 * - **rolagem** em vez de paginação: no celular, virar página é o gesto mais
 *   difícil de todos.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Columns2,
  Expand,
  Loader2,
  Maximize2,
  Minimize2,
  Moon,
  Pencil,
  Sun,
  TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, ErroApi, type TextoCompleto } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  CORPO_DA_APRESENTACAO_PADRAO,
  CORPOS_DA_APRESENTACAO,
  FAMILIA_DA_FONTE,
  LARGURA_DA_TELA,
  aumentarCorpo,
  diminuirCorpo,
  formatoDoTexto,
} from '@miolos/core';
import type { FormatoDoTexto } from '@miolos/core';

export function Apresentacao() {
  const { id = '' } = useParams();

  const [texto, setTexto] = useState<TextoCompleto | null>(null);
  const [formato, setFormato] = useState<FormatoDoTexto>(formatoDoTexto(null));
  const [corpo, setCorpo] = useState<number>(CORPO_DA_APRESENTACAO_PADRAO);
  const [escuro, setEscuro] = useState(false);
  const [cheia, setCheia] = useState(false);
  /** `false` = tela toda (padrão); `true` = a largura da coluna salva no texto. */
  const [coluna, setColuna] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { texto: aberto } = await api.abrirTexto(id);
      setTexto(aberto);
      setFormato(formatoDoTexto(aberto.formato));
      setErro('');
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir este texto.');
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternarTelaCheia = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setCheia(true);
      } else {
        await document.exitFullscreen();
        setCheia(false);
      }
    } catch {
      /* Alguns navegadores recusam sem gesto do usuário; a leitura continua igual. */
    }
  };

  useEffect(() => {
    const aoSair = () => setCheia(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', aoSair);
    return () => document.removeEventListener('fullscreenchange', aoSair);
  }, []);

  if (erro) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 py-8">
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
        <Button asChild variant="contorno">
          <Link to="/textos">
            <ArrowLeft />
            Voltar para meus textos
          </Link>
        </Button>
      </div>
    );
  }

  if (!texto) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Preparando a leitura...</span>
      </div>
    );
  }

  const noPrimeiro = corpo <= CORPOS_DA_APRESENTACAO[0]!;
  const noUltimo = corpo >= CORPOS_DA_APRESENTACAO[CORPOS_DA_APRESENTACAO.length - 1]!;

  /*
   * A coluna estreita usa a largura salva no texto; quando o texto está em "tela
   * toda" (largura 0), a coluna cai no padrão de livro, 150 mm — voltar para zero
   * não estreitaria nada.
   */
  const larguraDaColuna = formato.larguraMm === LARGURA_DA_TELA ? 150 : formato.larguraMm;

  /*
   * Em **tela cheia** o corpo dobra.
   *
   * Tela cheia não é só tirar o menu do caminho: é o modo de quem vai ler de longe
   * — o pregador no púlpito, a turma na parede, a pessoa com a vista cansada. Nesse
   * modo o texto vale **duas vezes** o corpo escolhido; A−/A+ continuam funcionando
   * a partir daí, e o número na barra mostra o tamanho que está na tela, e não o da
   * conta.
   */
  const vezes = cheia ? 2 : 1;
  const corpoNaTela = corpo * vezes;

  return (
    /*
     * `-mx-3 sm:-mx-4` cancela a margem interna do layout (`main`), e a área de
     * leitura encosta nas bordas: a apresentação não é uma página do site, é a tela
     * inteira lendo. Sem essa correção sobram ~16 px de cada lado e a moldura do
     * cartão, que somados à coluna davam a impressão de página espremida.
     */
    <div
      className={cn(
        '-mx-3 min-h-[75vh] sm:-mx-4',
        escuro ? 'bg-neutral-900 text-neutral-100' : 'bg-white text-neutral-900',
      )}
    >
      {/* Barra de leitura: some do caminho, mas está sempre ao alcance. */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-border/40 bg-inherit px-3 py-2">
        <Button asChild variant="fantasma" tamanho="pequeno">
          <Link to={`/textos/${texto.id}`}>
            <Pencil />
            Editar
          </Link>
        </Button>

        <div className="ml-auto flex flex-wrap items-center gap-1">
          <Button
            variant="contorno"
            tamanho="pequeno"
            onClick={() => setCorpo((atual) => diminuirCorpo(atual))}
            disabled={noPrimeiro}
            title="Diminuir o texto"
          >
            A−
          </Button>
          <span className="min-w-[64px] text-center text-[11px] opacity-70">
            {corpoNaTela} pt{vezes > 1 ? ' (tela cheia)' : ''}
          </span>
          <Button
            variant="contorno"
            tamanho="pequeno"
            onClick={() => setCorpo((atual) => aumentarCorpo(atual))}
            disabled={noUltimo}
            title="Aumentar o texto"
          >
            A+
          </Button>
          <Button variant="contorno" tamanho="pequeno" onClick={() => setEscuro((valor) => !valor)}>
            {escuro ? <Sun /> : <Moon />}
            {escuro ? 'Fundo claro' : 'Fundo escuro'}
          </Button>
          <Button
            variant="contorno"
            tamanho="pequeno"
            onClick={() => setColuna((valor) => !valor)}
            title={coluna ? 'Usar a tela toda' : 'Voltar à largura de coluna do texto'}
          >
            {coluna ? <Expand /> : <Columns2 />}
            {coluna ? 'Tela toda' : 'Coluna estreita'}
          </Button>
          <Button variant="contorno" tamanho="pequeno" onClick={() => void alternarTelaCheia()}>
            {cheia ? <Minimize2 /> : <Maximize2 />}
            {cheia ? 'Sair' : 'Tela cheia'}
          </Button>
        </div>
      </div>

      <article
        className="w-full px-4 py-8 sm:px-6 lg:px-10"
        style={{ maxWidth: coluna ? `${larguraDaColuna}mm` : 'none', marginInline: coluna ? 'auto' : undefined }}
      >
        <h1
          className="mb-8 font-semibold leading-tight"
          style={{ fontFamily: FAMILIA_DA_FONTE[formato.fonte], fontSize: `${corpoNaTela * 1.15}pt` }}
        >
          {texto.titulo}
        </h1>
        <div
          className="texto-apresentacao"
          style={{ fontFamily: FAMILIA_DA_FONTE[formato.fonte], fontSize: `${corpoNaTela}pt`, lineHeight: 1.65 }}
          /* O HTML vem limpo do servidor (`limparHtml`) — é o mesmo conteúdo do editor. */
          dangerouslySetInnerHTML={{ __html: texto.conteudo }}
        />
        <p className="mt-12 text-center text-[11px] opacity-50">Fim do texto.</p>
      </article>
    </div>
  );
}
